import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import emailjs from '@emailjs/browser';

interface ContactInfo {
  email: string;
  phone: string;
  address: string;
  mapUrl?: string;
}

const ContactInfoManager: React.FC = () => {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: '',
    phone: '',
    address: '',
    mapUrl: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const res = await fetch(process.env.REACT_APP_API_URL + '/api/contact-info', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await res.json();
        if (res.ok) {
          setContactInfo({
            email: data.email,
            phone: data.phone,
            address: data.address,
            mapUrl: data.mapUrl || '',
          });
        } else {
          setError(data.message || 'Error fetching contact information.');
        }
      } catch (err) {
        console.error('Error fetching contact info:', err);
        setError('Error fetching contact information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactInfo({ ...contactInfo, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You need to be logged in to update contact information.');
        return;
      }

      const res = await fetch(process.env.REACT_APP_API_URL + '/api/contact-info', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(contactInfo),
      });

      const data = await res.json();
      if (res.ok) {
        setSuccess('Contact information updated successfully!');

        // Send email notification to your email
        emailjs.send(
          'service_3tx8jli', // Your EmailJS service ID
          'template_2nq3cmj', // Your EmailJS template ID (update with the actual ID from EmailJS)
          {
            email: contactInfo.email,
            phone: contactInfo.phone,
            address: contactInfo.address,
            mapUrl: contactInfo.mapUrl || 'Not provided',
          },
          '2znub3nsdivNcJHnz' // Your EmailJS user ID
        ).then(
          (result) => {
            console.log('Email notification sent successfully:', result.text);
          },
          (error) => {
            console.error('Failed to send email notification:', error.text);
            setError('Contact info updated, but failed to send email notification.');
          }
        );
      } else {
        setError(data.message || 'Error updating contact information.');
      }
    } catch (err) {
      console.error('Error updating contact info:', err);
      setError('Error updating contact information. Please try again.');
    }
  };

  return (
    <div className="modern-form">
      <h2 className="dashboard-title text-center mb-5">Manage Contact Information</h2>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={contactInfo.email}
                onChange={handleChange}
                placeholder="Enter email"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={contactInfo.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formAddress">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="address"
                value={contactInfo.address}
                onChange={handleChange}
                placeholder="Enter address"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formMapUrl">
              <Form.Label>Google Maps Embed URL (Optional)</Form.Label>
              <Form.Control
                type="text"
                name="mapUrl"
                value={contactInfo.mapUrl}
                onChange={handleChange}
                placeholder="Enter Google Maps embed URL"
              />
            </Form.Group>

            <div className="text-center">
              <Button variant="primary" type="submit" className="modern-btn">
                Update Contact Information
              </Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );
};

export default ContactInfoManager;